import { MenuNewWindow, NoticeLoading } from '@fs/zion-icon'
import {
  getMostRecentFieldValue,
  getNestedSubelementsByType,
  getPersonName,
  getPrimaryRelationshipText,
  getRelationshipToPrimaryPersonText,
  useSlsStateContext,
} from '@fs/zion-sls'
import {
  Divider,
  IconButton,
  Image,
  LayoutBand,
  ListItem,
  Paragraph,
  ResponsiveTable,
  ResponsiveTableRow,
  Row,
  Separator,
  usePageWidth,
} from '@fs/zion-ui'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import searchRecordImg from '../../../../images/search-record.png'
import useRecords from '../../hooks/useRecords'
import { superDeduper } from '../editPanels/utils/editPanelDispatchBuilders'
import PersonTableRowDisplay from './PersonTableRowDisplay'
import IndexedDataHeader, { NamesNotInRecordsHeader } from './SelectPersonHeaders'

const SelectPersonBottomSheet = () => {
  const [t] = useTranslation()
  const pageWidth = usePageWidth()
  const size = pageWidth({ default: 'default', sm: 'sm', md: 'md', lg: 'lg', xl: 'xl', xxl: 'xxl' })
  const zionSlsState = useSlsStateContext()
  const { currentImageArk } = zionSlsState
  const { thumbnails } = useSharedBrowserStateContext()
  const personIdsOrderForImage = zionSlsState.personOrder?.find((po) => po.partitionKey === currentImageArk)

  const { allRecords, floatingPersons, imageHasData, noPeople } = useRecords({
    images: zionSlsState?.images,
    currentImageArk: zionSlsState?.currentImageArk,
    personIdsOrder: personIdsOrderForImage?.personIds,
    thumbnails,
  })
  const persons = useMemo(() => {
    return allRecords?.flatMap((record) =>
      record?.subElements?.filter((sub) => sub?.elementType === 'PERSON' && sub?.partitionKey === currentImageArk)
    )
  }, [allRecords, currentImageArk])
  const events = useMemo(
    () => persons?.flatMap((person) => person?.subElements.filter((element) => element?.elementType === 'EVENT')),
    [persons]
  )
  const primaryEvent = events?.find((event) => event.primary)
  const otherEvents = useMemo(() => events?.filter((event) => !event.primary), [events])
  const otherEventsFormatted = useMemo(() => {
    return superDeduper(
      otherEvents?.flatMap((event) => ({ type: event.eventType, displayLabel: event.displayLabel, events: [] }))
    )
  }, [otherEvents])
  otherEvents.forEach((otherEvent) => {
    const relatedEventLabel = otherEventsFormatted.find((label) => label.type === otherEvent.eventType)
    relatedEventLabel.events.push(otherEvent)
  })

  const labelTranslations = useMemo(() => {
    return {
      name: t('edit-essential-info.title', 'Name'),
      raceOrColor: t('edit-essential-info.race-or-color', 'Race Or Color'),
      sex: t('edit-essential-info.sex-code', 'Sex'),
      age: t('edit-age.heading', 'Age'),
      relationships: t('edit-relationships.heading', 'Relationships'),
      primaryEvent: primaryEvent?.displayLabel,
    }
  }, [primaryEvent, t])

  const MAX_OTHER_EVENT_COLUMNTS = 4

  const getOtherEventLabels = useCallback(() => {
    return otherEventsFormatted.map((otherEvent) => otherEvent.displayLabel).slice(0, MAX_OTHER_EVENT_COLUMNTS)
  }, [otherEventsFormatted])

  const columnHeaders = useMemo(() => {
    return {
      default: {
        labels: [labelTranslations.name, labelTranslations.raceOrColor],
      },
      sm: {
        labels: [labelTranslations.name, labelTranslations.raceOrColor, labelTranslations.sex],
      },
      md: {
        labels: [labelTranslations.name, labelTranslations.raceOrColor, labelTranslations.sex, labelTranslations.age],
      },
      lg: {
        labels: [
          labelTranslations.name,
          labelTranslations.raceOrColor,
          labelTranslations.sex,
          labelTranslations.age,
          labelTranslations.relationships,
        ],
      },
      xl: {
        labels: [
          labelTranslations.name,
          labelTranslations.raceOrColor,
          labelTranslations.sex,
          labelTranslations.age,
          labelTranslations.relationships,
          labelTranslations.primaryEvent,
        ],
      },
      xxl: {
        labels: [
          labelTranslations.name,
          labelTranslations.raceOrColor,
          labelTranslations.sex,
          labelTranslations.age,
          labelTranslations.relationships,
          labelTranslations.primaryEvent,
          ...getOtherEventLabels(),
        ],
      },
    }
  }, [labelTranslations, getOtherEventLabels])

  const getRelationshipText = ({ person, record }) => {
    const relationshipText = `${getRelationshipToPrimaryPersonText(person, zionSlsState.types, record, t)}`
    return getPrimaryRelationshipText(null, relationshipText)
  }

  const columnCount = useMemo(() => {
    return columnHeaders[size].labels.length
  }, [columnHeaders, size])

  return (
    <>
      <IndexedDataHeader />
      <LayoutBand>
        {imageHasData && (
          <>
            <ResponsiveTable gutters="xxs" atSizeColumns={columnHeaders} totalColumnCount={columnCount}>
              {allRecords.map((record) => {
                const recordPersons = record?.subElements?.filter(
                  (sub) => sub?.elementType === 'PERSON' && sub?.partitionKey === currentImageArk
                )
                const primaryPerson = recordPersons.find((person) => person.primary)
                const allPersonsMentioned = getNestedSubelementsByType(record, 'PERSON')
                const otherPersonsMentioned =
                  allPersonsMentioned.filter((person) => person?.partitionKey !== currentImageArk) || []
                const foundGivenOfPrimary = getNestedSubelementsByType(primaryPerson, 'NAME_GN')
                const givenNameOfPrimary = getMostRecentFieldValue(foundGivenOfPrimary?.[0]) || ''
                return (
                  <React.Fragment key={record.id}>
                    {recordPersons.map((person, i) => (
                      <PersonTableRowDisplay
                        key={`${person.id}-${record.id}`}
                        person={person}
                        record={record}
                        i={i}
                        primaryEvent={primaryEvent}
                        otherEventsFormatted={otherEventsFormatted}
                        MAX_OTHER_EVENT_COLUMNTS={MAX_OTHER_EVENT_COLUMNTS}
                      />
                    ))}
                    {otherPersonsMentioned?.length > 0 && (
                      <ResponsiveTableRow
                        key={`other-persons-mentioned-${record.id}`}
                        column1span={columnCount}
                        column1={
                          <>
                            <Paragraph size="sm">
                              {t('other-persons-mentioned.mentioned.heading', 'Others mentioned in this record')}
                            </Paragraph>
                            <Paragraph size="xs">
                              {t(
                                'other-persons-mentioned.these-are-referenced.sub-heading',
                                'These are referenced in relation to {givenName} on other pages',
                                { givenName: givenNameOfPrimary }
                              )}
                            </Paragraph>
                          </>
                        }
                      >
                        {otherPersonsMentioned.map((otherPerson) => {
                          const goToPerson = () => {
                            window.open(
                              `/ark:/61903/${otherPerson.partitionKey}?view=index&personArk=/ark:/61903/${otherPerson.id}&action=view`
                            )
                          }
                          return (
                            <ListItem
                              key={otherPerson.id}
                              onClick={goToPerson}
                              overline={getRelationshipText({ person: otherPerson, record })}
                              primaryText={getPersonName({ person: otherPerson })}
                              endElement={<IconButton Icon={MenuNewWindow} onClick={goToPerson} />}
                              // this is necessary to align this name with the others
                              // eslint-disable-next-line react/jsx-no-useless-fragment
                              startElement={<></>}
                              dense
                            />
                          )
                        })}
                      </ResponsiveTableRow>
                    )}
                  </React.Fragment>
                )
              })}
            </ResponsiveTable>

            {/* !hasRecords && floatingPersons?.length === 0 && !isJiapu */}

            {floatingPersons && floatingPersons?.length > 0 && (
              <>
                <Divider />
                <NamesNotInRecordsHeader />
                <Separator size="xs" />
                <ResponsiveTable gutters="xxs" atSizeColumns={columnHeaders} totalColumnCount={columnCount}>
                  <>
                    {floatingPersons
                      .sort((a, b) => (a.primary > b.primary ? -1 : 1))
                      .map((person, i) => (
                        <PersonTableRowDisplay key={person.id} person={person} i={i} primaryEvent={primaryEvent} />
                      ))}
                  </>
                </ResponsiveTable>
              </>
            )}
          </>
        )}
        {!zionSlsState.loading && !imageHasData && (
          <LayoutBand>
            <Separator />
            <Row alignX="center">
              <Image src={searchRecordImg} alt="" maxWidth={250} />
            </Row>
            <Paragraph size="sm" centered>
              {t('select-person.subheading.not-indexed', 'This image has not been indexed.')}
            </Paragraph>
          </LayoutBand>
        )}
        {noPeople && zionSlsState.loading && (
          <Row alignX="center">
            <NoticeLoading size="md" />
          </Row>
        )}
      </LayoutBand>
    </>
  )
}

export default SelectPersonBottomSheet
